import * as React from 'react';
import {useState, useEffect} from "react";
import {Grid, Container, TextField, Button, CircularProgress, Alert,  Table,
        TableHead, TableRow, TableCell, TableBody, Stack, FormControl, InputLabel, MenuItem, Box, Select } from '@mui/material';
import {useParams} from 'react-router-dom';
import {useNavigate, useLocation} from 'react-router-dom'
import Typography from '@mui/material/Typography';
import axios from 'axios';
import {validateUserForm} from "./formValidation";
import {getLocalToken, logout} from "../utils";
import {LocalizationProvider, DatePicker} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit';
import AddFamilyMemberDialog from "./dialogs/addFamilyDialog";
import EditFamilyMemberDialog from "./dialogs/editFamilyDialog";
import DeleteFamilyMemberDialog from "./dialogs/deleteFamilyDialog";
import DeleteUserDialog from "./dialogs/deleteUserDialog";
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import PregnantWomanIcon from '@mui/icons-material/PregnantWoman';

export default function EditUser() {
    const {state} = useLocation();
    const {id} = useParams();
    const [fname, setFname] = useState(state ? state.firstName : '');
    const [lname, setLname] = useState(state ? state.lastName : '');
    const [email, setEmail] = useState(state ? state.email : '');
    const [dateOfBirth, setDateOfBirth] = useState(state ? state.dateOfBirth : '');
    const [phoneNumber, setPhoneNumber] = useState(state ? state.phoneNumber : '');
    const [socialNumber, setSocialNumber] = useState(state ? state.socialNumber : '');
    const [city, setCity] = useState(state ? state.city : '');
    const [street, setStreet] = useState(state ? state.street : '');
    const [houseNumber, setHouseNumber] = useState(state ? state.houseNumber : '');
    const [password, setPassword] = useState('');
    const [gender, setGender] = useState('');
    const formRef = React.useRef();
    const [serverError, setServerError] = useState('');
    const [loading, setLoading] = useState(false);
    const [familyLoading, setFamilyLoading] = useState(false);
    const [familyMembers, setFamilyMembers] = useState([]);
    const [addFamilyDialogOpen, setAddFamilyDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteFamilyDialogOpen, setDeleteFamilyDialogOpen] = useState(false);
    const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
    const [memberToEdit, setMemberToEdit] = useState(null);
    const [memberToDelete, setMemberToDelete] = useState(null);
    const [formErrors, setFormErros] = useState();
    const navigate = useNavigate();
    const token = getLocalToken();

    useEffect(() => {
        axios.get(`/admin/api/v1/users/${id}`, {headers: {"Authorization": `Bearer ${token}`}})
            .then((res) => {
                if (res.status === 200) {
                    if (res.data.payload) {
                        setFname(res.data.payload.firstName);
                        setLname(res.data.payload.lastName);
                        setEmail(res.data.payload.email);
                        setPhoneNumber(res.data.payload.phoneNumber || null);
                        setSocialNumber(res.data.payload.socialNumber || null);
                        setCity(res.data.payload.city);
                        setStreet(res.data.payload.street);
                        setHouseNumber(res.data.payload.houseNumber);
                        setDateOfBirth(res.data.payload.dateOfBirth || null);
                        setGender(res.data.payload.gender);
                        setLoading(false);
                    }
                    fetchUserFamily()
                }
            })
            .catch((err) => {
                setLoading(false);
                console.log(err);
                if (err.response.status === 401) {
                    logout();
                    navigate('/login');
                }
            });
    }, []);

    const fetchUserFamily = (userUID) => {
        setFamilyLoading(true);
        axios.get(`/admin/api/v1/users/${id}/family`, {headers: {"Authorization": `Bearer ${token}`}})
            .then((res) => {
                if (res.status === 200 && res.data.payload) {
                    setFamilyMembers(res.data.payload);
                } else {
                    setFamilyMembers([]);
                }
            })
            .catch((err) => {
                setServerError('Failed to load family members.');
            })
            .finally(() => {
                setFamilyLoading(false);
            });
    };

    const handleSaveBtnClick = () => {
        if (!formRef.current.reportValidity()) {
            return
        }
        let formErrors = validateUserForm({fname, lname, password, email, street, city});
        setFormErros(formErrors);
        if (formErrors.fname.err || formErrors.lname.err || formErrors.password.err || formErrors.email.err || formErrors.street.err
            || formErrors.city.err) {
            return
        }
        setLoading(true);

        const data = {firstName: fname, lastName: lname, email, password, phoneNumber, socialNumber, city, street, gender, houseNumber, dateOfBirth};
        axios.patch(`/admin/api/v1/users/${id}/edit`, data, {
            headers: {Authorization: `Bearer ${token}`}
        })
            .then(res => {
                if (res && res.status == 200) {
                    navigate('/users')
                }
            })
            .catch(err => {
                console.log(err);
                setLoading(false);
                if (err.response && err.response.data.error) {
                    setServerError(<Alert severity="error">Failed to update
                        user. {err.response.data.error.message}</Alert>)
                } else {
                    setServerError(<Alert severity="error">Failed to update user. Something went wrong. Please contact
                        support.</Alert>)
                }
            })
    };

    const handleAddMember = async (memberData) => {
        try {
            const response = await fetch(`/admin/api/v1/users/${id}/family`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(memberData),
            });
            const savedMember = await response.json();

            fetchUserFamily();
            setAddFamilyDialogOpen(false);
        } catch (error) {
            console.error("Error adding family member", error);
        }
    };

    const handleEditClick = (member) => {
        console.log('member -->', member)
        setMemberToEdit(member);
        setEditDialogOpen(true);
    };

    const handleSaveEditedMember = async (updatedMember) => {
        try {
            const response = await fetch(`/admin/api/v1/users/family/${updatedMember.uid}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(updatedMember),
            });
            const savedMember = await response.json();

            fetchUserFamily();
            setEditDialogOpen(false);
        } catch (error) {
            console.error("Error updating family member", error);
        }
    };

    const handleDeleteClick = (member) => {
        setMemberToDelete(member);
        setDeleteFamilyDialogOpen(true);
    };

    const handleDeleteConfirmed = async (member) => {
        try {
            await fetch(`/admin/api/v1/users/family/${member.uid}`, {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            fetchUserFamily();
            setDeleteFamilyDialogOpen(false);
        } catch (error) {
            console.error("Error deleting family member", error);
        }
    };

    const handleUserDeleteConfirmed = async (member) => {
        try {
            await fetch(`/admin/api/v1/users/${id}`, {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });
            setDeleteUserDialogOpen(false);
            navigate('/users')
        } catch (error) {
            console.error("Error deleting  user", error);
        }
    };

    const familyTbl = (
        <>
            <br/>
            <Grid container justifyContent="space-between">
                <Grid item>
                    <Typography variant="h5" gutterBottom style={{color: '#1d1d1d'}}>
                        Familienmitglieder
                    </Typography>
                </Grid>
                <Grid item>
                    <Button variant="text" onClick={() => {setAddFamilyDialogOpen(true)}}>
                        <AddIcon/>
                        Mitglied hinzufügen
                    </Button>
                </Grid>
            </Grid>
            <Table>
                <TableHead>
                    <TableRow sx={{backgroundColor: 'rgba(0, 0, 0, 0.05)'}}>
                        <TableCell>Vorname</TableCell>
                        <TableCell>Nachname</TableCell>
                        <TableCell>Geburtsdatum</TableCell>
                        <TableCell>Sozialversicherungsnummer</TableCell>
                        <TableCell>Erstellt</TableCell>
                        <TableCell></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {familyMembers && familyMembers.length > 0 ? (
                        familyMembers.map((member, index) => (
                            <TableRow key={index}>
                                <TableCell>{member.firstName}</TableCell>
                                <TableCell>{member.lastName}</TableCell>
                                <TableCell>
                                    {member.dateOfBirth}
                                </TableCell>
                                <TableCell>
                                    {member.socialNumber}
                                </TableCell>
                                <TableCell>{member.created}</TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={2}>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            size="small"
                                            startIcon={<EditIcon />}
                                            onClick={() => {handleEditClick(member)}}
                                            sx={{ fontSize: '0.75rem', minWidth: '64px' }}
                                        >
                                            Bearbeiten
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            startIcon={<DeleteIcon />}
                                            onClick={() => {handleDeleteClick(member)}}
                                            sx={{ fontSize: '0.75rem', minWidth: '64px' }}
                                        >
                                            Löschen
                                        </Button>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={5} align="center">
                                Keine Familienmitglieder gefunden.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </>
    );

    return (
        <div>
            <Grid container>
                <Grid item xs={5} md={5}>
                    <Typography variant="h5" gutterBottom>
                        Benutzer: {email} ausgabe
                    </Typography>
                </Grid>
            </Grid>
            <br/>
            <Grid container spacing={2}>
                <Grid item size={{xs: 12, sm: 8, md: 4}}>
                    <form ref={formRef}>
                        <TextField
                            name="userId"
                            variant="standard"
                            fullWidth
                            id="id"
                            label="Id"
                            disabled
                            value={id}
                            sx={{m: 1}}
                        />
                        <TextField
                            name="firstName"
                            variant="standard"
                            required
                            fullWidth
                            id="firstName"
                            label="Vorname"
                            autoFocus
                            value={fname || ''}
                            onChange={e => setFname(e.target.value)}
                            helpertext={formErrors && formErrors.fname.err && formErrors.fname.errMsg}
                            error={formErrors && formErrors.fname.err}
                            sx={{m: 1}}
                        />
                        <TextField
                            name="lastName"
                            variant="standard"
                            required
                            fullWidth
                            id="lastName"
                            label="Nachname"
                            value={lname || ''}
                            helpertext={formErrors && formErrors.lname.err && formErrors.lname.errMsg}
                            error={formErrors && formErrors.lname.err}
                            onChange={e => setLname(e.target.value)}
                            sx={{m: 1}}
                        />
                        <TextField
                            name="email"
                            type="email"
                            variant="standard"
                            fullWidth
                            id="email"
                            label="Email"
                            value={email || ''}
                            helperText={formErrors && formErrors.email?.err && formErrors.email?.errMsg}
                            error={formErrors && formErrors.email.err}
                            onChange={e => setEmail(e.target.value)}
                            sx={{m: 1}}
                        />

                        <FormControl variant="standard" fullWidth sx={{marginLeft: '8px'}}>
                            <InputLabel>Gender</InputLabel>
                            <Select
                                value={gender}
                                name="gender"
                                label="Geschlecht"
                                onChange={(e) => setGender(e.target.value)}
                            >
                                <MenuItem value="male">
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <MaleIcon fontSize="small" />
                                        <Box sx={{ ml: 1 }}>Männlich</Box>
                                    </Box>
                                </MenuItem>
                                <MenuItem value="female">
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <FemaleIcon fontSize="small" />
                                        <Box sx={{ ml: 1 }}>Weiblich</Box>
                                    </Box>
                                </MenuItem>
                                <MenuItem value="pregnant">
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <PregnantWomanIcon fontSize="small" />
                                        <Box sx={{ ml: 1 }}>Schwanger</Box>
                                    </Box>
                                </MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            name="phoneNumber"
                            type="text"
                            variant="standard"
                            fullWidth
                            value={phoneNumber || ''}
                            id="phone"
                            label="Telefonnummer"
                            onChange={e => setPhoneNumber(e.target.value)}
                            sx={{m: 1}}
                        />

                        <TextField
                            name="socialNumber"
                            type="text"
                            variant="standard"
                            fullWidth
                            value={socialNumber || ''}
                            id="socialNumber"
                            label="Sozialversicherungsnummer"
                            onChange={e => setSocialNumber(e.target.value)}
                            sx={{m: 1}}
                        />

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Geburtsdatum"
                                name="dateOfBirth"
                                value={dayjs(dateOfBirth)}
                                onChange={(newValue) => setDateOfBirth(newValue)}
                                format="DD-MM-YYYY"
                                disableMaskedInput
                                slotProps={{
                                    textField: {
                                        variant: "standard",
                                        fullWidth: true,
                                    },
                                }}
                                sx={{marginLeft: '8px'}}
                            />
                        </LocalizationProvider>

                        <TextField
                            name="city"
                            type="text"
                            variant="standard"
                            fullWidth
                            value={city || ''}
                            id="city"
                            label="Stadt"
                            helperText={formErrors && formErrors.city?.err && formErrors.city?.errMsg}
                            error={formErrors && formErrors.city.err}
                            onChange={e => setCity(e.target.value)}
                            sx={{m: 1}}
                        />
                        <TextField
                            name="street"
                            type="text"
                            variant="standard"
                            fullWidth
                            helperText={formErrors && formErrors.street?.err && formErrors.street?.errMsg}
                            error={formErrors && formErrors.street.err}
                            value={street || ''}
                            id="street"
                            label="Straße"
                            onChange={e => setStreet(e.target.value)}
                            sx={{m: 1}}
                        />
                        <TextField
                            label="Hausnummer"
                            variant="standard"
                            name="houseNumber"
                            value={houseNumber || ''}
                            onChange={e => setHouseNumber(e.target.value)}
                            fullWidth
                            sx={{m: 1}}
                        />
                        <TextField
                            name="password"
                            type="password"
                            variant="standard"
                            fullWidth
                            value={password || ''}
                            id="password"
                            label="Kennwort ändern"
                            onChange={e => setPassword(e.target.value)}
                            sx={{m: 1}}
                        />
                    </form>
                </Grid>
            </Grid>
            <br/>
            { familyTbl }
            <div style={{color: 'red', marginBottom: '20px'}}>{serverError}</div>
            <Grid container justifyContent="space-between">
                <Grid item>
                    <Button variant="contained" color="success" disabled={loading} onClick={handleSaveBtnClick}>
                        {loading && (
                            <CircularProgress
                                style={{color: 'rgba(0, 0, 0, 0.4)', marginRight: '10px'}}
                                size={20}
                            />
                        )}
                        <span>Speichern</span>
                    </Button>
                </Grid>
                <Grid item>
                    <Button variant="outlined" color="error" onClick={ () => {setDeleteUserDialogOpen(true)}} sx={{marginRight: '15px'}}>
                        Benutzer löschen
                    </Button>
                    <Button variant="outlined" onClick={() => navigate('/users')}>
                        Stornieren
                    </Button>
                </Grid>
            </Grid>
            <AddFamilyMemberDialog
                open={addFamilyDialogOpen}
                onClose={() => setAddFamilyDialogOpen(false)}
                onCreate={handleAddMember}
            />
            <EditFamilyMemberDialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                onSave={handleSaveEditedMember}
                member={memberToEdit}
            />
            <DeleteFamilyMemberDialog
                open={deleteFamilyDialogOpen}
                onClose={() => setDeleteFamilyDialogOpen(false)}
                onDelete={handleDeleteConfirmed}
                member={memberToDelete}
            />
            <DeleteUserDialog
                open={deleteUserDialogOpen}
                onClose={() => setDeleteUserDialogOpen(false)}
                onDelete={handleUserDeleteConfirmed}
                user={{fname, lname, email}}
            />
        </div>
    );
}